<template>
  <div class="addPackageWrapper">
    <q-form @submit="onSubmit" class="form">
      <div class="form__item">
        <CustomSelect
          :options="placementTypesOptions"
          label="Тип помещения"
          optionValueKey="placement_type_id"
          optionLabelKey="placement_type_name"
          :required="true"
          placeholder="Выберите тип помещения"
          :value="formData.placement_type_id || ''"
          @change="(value, row) => onChange('placement_type_id', value, row)"
        />
      </div>
      <div class="form__item">
        <div class="select-with-button">
          <CustomSelect
            :options="workSetsOptions"
            label="Конструктив"
            optionValueKey="work_set_id"
            optionLabelKey="work_set_name"
            :required="true"
            placeholder="Выберите конструктив"
            :value="formData.work_set_id || ''"
            @change="(value, row) => onChange('work_set_id', value, row)"
            class="select-with-button__select"
          />
          <q-btn
            @click="openCreateWorksetModal"
            class="select-with-button__btn"
            color="primary"
            icon="add"
            round
            size="sm"
            flat
          />
        </div>
      </div>

      <div v-if="selectedWorkSet" class="work-set-info">
        <div class="info-item">
          <span class="info-label">Ед.изм:</span>
          <span class="info-value">{{ selectedWorkSet.unit_name }}</span>
        </div>
        <div class="info-item">
          <span class="info-label">Формула:</span>
          <span class="info-value">{{ selectedWorkSet.formula }}</span>
        </div>
      </div>

      <div class="form__item">
        <CustomInputDropdown
          label="Поиск материала"
          placeholder="Поиск"
          :value="materialSearchValue"
          :options="materialOptions"
          optionValueKey="material_id"
          optionLabelKey="label"
          :loading="materialSearchLoading"
          @change_input="onMaterialSearch"
          @select_item="onMaterialSelect"
        />
      </div>

      <div class="form__item">
        <div
          v-if="!formData.material_set_id || !createdMaterialSetItems"
          class="select-with-button"
        >
          <CustomSelect
            :options="materialSetsOptions"
            label="Набор"
            optionValueKey="material_set_id"
            optionLabelKey="set_name"
            placeholder="Выберите набор"
            :value="formData.material_set_id || ''"
            @change="(value, row) => onChange('material_set_id', value, row)"
            class="select-with-button__select"
          />
          <q-btn
            @click="openCreateMaterialSetModal"
            class="select-with-button__btn"
            color="primary"
            icon="add"
            round
            size="sm"
            flat
          />
        </div>
      </div>

      <div
        v-if="!!formData.material_set_id || createdMaterialSetItems.length > 0"
        class="material-set-info"
      >
        <div class="material-set-header">
          <div class="info-title">Набор: {{ selectedSet?.set_name }}</div>
          <q-btn
            @click="clearMaterialSet"
            class="clear-set-btn"
            color="negative"
            icon="close"
            round
            size="sm"
            flat
          />
        </div>
        <div
          v-for="(item, index) in materialSetItems"
          :key="item.material_id + '_' + index"
          class="material-item"
        >
          <div class="material-number">
            {{ index + 1 }}.{{ item.material_name || item.set_name }}
          </div>
          <div class="material-details">
            <div class="detail-item">
              <span class="detail-label">Расход</span>
              <span class="detail-value">{{ item.consumption }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Формула</span>
              <span class="detail-value">{{ item.formula }}</span>
            </div>
          </div>
        </div>
      </div>

      <CustomCheckbox
        label="По умолчанию"
        :value="formData.is_default"
        @change="(value) => onChange('is_default', value)"
      />
      <CustomModalFooter :btn-loading="loading" submit-btn-text="Занести" />
    </q-form>
  </div>
</template>

<script setup>
import { ref, reactive, computed } from "vue"
import { useQuery } from "@tanstack/vue-query"
import CustomSelect from "@/components/form/CustomSelect"
import CustomModalFooter from "@/components/modal/modals/modal-footer"
import CustomInputDropdown from "@/components/form/CustomInputDropdown"
import CustomCheckbox from "@/components/form/CustomCheckbox";

import { useAppStore } from "@/services/store/app"
import { useCustomSnackbar } from "@/_helpers/hooks/useCustomSnackbar"
import {
  getPlacementTypes,
  getWorkSets,
  searchMaterials,
  getMaterialSets,
  addPackageEntry,
  getSetInfo,
} from "@/views/PackageEntry/services"

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  closeModal: {
    type: Function,
    default: () => {},
  },
});

const appStore = useAppStore();
const { showSucceedSnackbar, showErrorSnackbar } = useCustomSnackbar();

const loading = ref(false);
const controller = ref(null);
const selectedSet = ref(null);
const materialSearchLoading = ref(false);
const materialSearchValue = ref("");
const materialOptions = ref([]);
const setInfo = ref([]);
const formData = reactive({
  placement_type_id: "",
  work_set_id: "",
  material_id: "",
  material_set_id: "",
  is_default: true,
});

const presetKitId = computed(() => props.data.params?.preset_kit_id);

const { data: placementTypesData } = useQuery({
  queryKey: computed(() => ["placementTypes", presetKitId.value]),
  queryFn: () => getPlacementTypes(presetKitId.value),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});

const { data: workSetsData, refetch: refetchWorkSets } = useQuery({
  queryKey: computed(() => ["workSets", "modal"]),
  queryFn: () => getWorkSets(),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});


const { data: materialSetItemsData } = useQuery({
  queryKey: computed(() => ["materialSetItems", formData.material_id]),
  queryFn: () =>
    formData.material_id
      ? getMaterialSets(formData.material_id)
      : Promise.resolve({ data: [] }),
  enabled: computed(() => !!formData.material_id),
  refetchOnWindowFocus: false,
  initialData: { data: [] },
});


const placementTypesOptions = computed(
  () => placementTypesData.value?.data || []
);
const workSetsOptions = computed(() => workSetsData.value?.data || []);
const materialSetsOptions = computed(() => materialSetItemsData.value?.data || []);
const createdMaterialSetItems = ref([]);

const materialSetItems = computed(() => {
  const created = createdMaterialSetItems.value;
  const existing = setInfo.value || [];
  return created.length > 0 ? created : existing;
});
const selectedWorkSet = computed(() => {
  if (!formData.work_set_id || !workSetsOptions.value.length) return null;
  return workSetsOptions.value.find(
    (item) => item.work_set_id === formData.work_set_id
  );
});

const getSetMaterials = async () => {
  const res = await getSetInfo(formData.material_set_id);
  if(!res) return
  setInfo.value = res?.materials || [];
  selectedSet.value = {material_set_id: res.material_set_id, set_name: res.set_name}
}

const onChange = (key, value, row) => {
  formData[key] = value;

  if (key === "material_id") {
    formData.material_set_id = "";
    createdMaterialSetItems.value = [];
  }

  if (key === "material_set_id") {
    createdMaterialSetItems.value = [];
    selectedSet.value = row || null

    if(value) {
      getSetMaterials()
    }
  }
};

const clearMaterialSet = () => {
  formData.material_set_id = "";
  createdMaterialSetItems.value = [];
  selectedSet.value = null;
  setInfo.value = null;
};

const onMaterialSearch = async (searchValue) => {
  materialSearchValue.value = searchValue

  if (formData.material_set_id) {
    formData.material_set_id = ""
  }

  if (!searchValue || searchValue.length < 3) {
    materialOptions.value = []
    return
  }

  if (controller.value) {
    controller.value.abort();
  }
  controller.value = new AbortController();

  materialSearchLoading.value = true
  const res = await searchMaterials(searchValue, controller.value.signal)
  if (!res) {
    materialSearchLoading.value = false
    return
  }
  materialOptions.value = res || []
  materialSearchLoading.value = false
}

const onMaterialSelect = (materialId, materialData) => {
  formData.material_id = materialId;
  formData.material_set_id = "";
  materialSearchValue.value = materialData.material_name;
  materialOptions.value = [];
};

const openCreateWorksetModal = () => {
  appStore.showSecondaryModal("CreateWorkset", {
    title: "Создать конструктив",
    onClose: async (workSetId) => {
      await refetchWorkSets();
      if (workSetId) {
        formData.work_set_id = workSetId;
      }
    },
  });
};

const openCreateMaterialSetModal = () => {
  if (!formData.work_set_id) {
    showErrorSnackbar("Сначала выберите конструктив");
    return;
  }

  appStore.showSecondaryModal("CREATE_MATERIAL_SET", {
    title: "Создать набор",
    params: {
      workSetId: formData.work_set_id,
    },
    data: selectedWorkSet.value,
    onClose: (materialSet) => {
      const materialSetId = materialSet?.material_set_id;
      selectedSet.value = materialSet
      if (materialSetId) {
        formData.material_set_id = materialSetId;
      }
      if (materialSet) {
        createdMaterialSetItems.value = materialSet.materials || [];
      }
    },
  });
};

const onSubmit = async () => {
  if (!formData.placement_type_id || !formData.work_set_id) {
    return
  }
  if (!formData.material_set_id) {
    showErrorSnackbar('Выберите или создайте набор')
  }

  loading.value = true
  const body = {
    preset_kit_id: presetKitId.value,
    placement_type_id: formData.placement_type_id,
    work_set_id: formData.work_set_id,
    material_set_id: formData.material_set_id,
    is_default: formData.is_default,
  }

  const res = await addPackageEntry(body)
  if (!res) {
    loading.value = false
    return
  }

  showSucceedSnackbar()

  if (props.data?.onSubmit) {
    props.data.onSubmit(res)
  }
  props.closeModal()
  loading.value = false
}
</script>

<style scoped lang="scss">
.addPackageWrapper {
  max-width: 700px;

  @media (max-width: 768px) {
    max-width: 100%;
  }
}
.form {
  display: flex;
  flex-direction: column;
  min-width: 350px;
  gap: 15px;
  margin-top: 15px;

  @media (max-width: 428px) {
    min-width: 100%;
  }
}

.work-set-info {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e0e0e0;

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .info-label {
      font-weight: 500;
      color: #666;
      font-size: 14px;
    }

    .info-value {
      font-weight: 600;
      color: #333;
      font-size: 14px;
    }
  }
}

.select-with-button {
  display: flex;
  align-items: flex-end;
  gap: 8px;

  &__select {
    flex: 1;
  }

  &__btn {
    margin-bottom: 2px;
  }
}

.material-set-info {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  border: 1px solid #e9ecef;

  .material-set-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .info-title {
    font-weight: 600;
    color: #333;
    font-size: 16px;
  }

  .clear-set-btn {
    margin-left: 8px;
  }

  .material-item {
    background-color: #ffffff;
    border-radius: 6px;
    padding: 12px;
    margin-bottom: 12px;
    border: 1px solid #e0e0e0;

    &:last-child {
      margin-bottom: 0;
    }

    .material-number {
      font-weight: 500;
      color: #333;
      font-size: 14px;
      margin-bottom: 8px;
    }

    .material-details {
      display: flex;
      gap: 16px;

      .detail-item {
        display: flex;
        flex-direction: column;
        gap: 2px;

        .detail-label {
          font-size: 12px;
          color: #666;
          font-weight: 500;
        }

        .detail-value {
          font-size: 13px;
          color: #333;
          font-weight: 600;
        }
      }
    }
  }
}
</style>
